import React, { useState } from 'react';
import styled from 'styled-components';
import { adminApi, SendConsentRequest } from '../services/adminApi';
import { CountryCodeSelect } from './CountryCodeSelect';
import { 
  validateOptionalEmail, 
  validateOptionalPhoneNumber 
} from '../utils/contactValidation';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  FormGroup,
  Label,
  Input,
  Select,
  AlertMessage,
  PrimaryButton,
  SecondaryButton,
} from '../styles/adminShared';

// Unique styled components for this modal
const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const PhoneInputRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
`;

const SendMethodGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SendMethodOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border: 2px solid ${props => props.$selected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#eff6ff' : 'white'};
  flex: 1;
  min-width: 140px;
  
  &:hover {
    border-color: #3b82f6;
  }
  
  input {
    display: none;
  }
`;

const MethodIcon = styled.span`
  font-size: 24px;
`;

const MethodInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const MethodName = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
`;

const MethodDesc = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const Section = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Unique success/result components
const SuccessBox = styled.div`
  background: #dcfce7;
  border: 1px solid #86efac;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SuccessTitle = styled.div`
  font-weight: 600;
  color: #166534;
  font-size: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UrlBox = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const UrlInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #86efac;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  background: white;
`;

const CopyButton = styled.button`
  padding: 10px 16px;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: #15803d;
  }
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0 0;
`;

interface SendConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  formTitle?: string;
  evidenceTypes?: string[];
  successRedirectUrl?: string;
  failureRedirectUrl?: string;
}

type SendMethod = 'EMAIL' | 'SMS' | 'BOTH' | 'NONE';

export const SendConsentModal: React.FC<SendConsentModalProps> = ({
  isOpen,
  onClose,
  formId,
  formTitle,
  evidenceTypes = ['signature'],
  successRedirectUrl,
  failureRedirectUrl,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAgeConfirmation, setShowAgeConfirmation] = useState(false);
  const [confirmedAdult, setConfirmedAdult] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    phoneNumber: '',
    gender: '' as '' | 'M' | 'F' | 'O' | 'N',
    dateOfBirth: '',
    sendMethod: 'EMAIL' as SendMethod,
    customMessage: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    // Reset age confirmation if DOB changes
    if (field === 'dateOfBirth') {
      setShowAgeConfirmation(false);
      setConfirmedAdult(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    
    // Gender is now optional - no validation needed
    
    // DOB validation - optional but if provided, check age
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 18) {
        setError('This platform currently does not support consent collection for individuals under 18 years of age');
        return false;
      }
    } else {
      // If no DOB provided, require adult confirmation
      if (!confirmedAdult) {
        setShowAgeConfirmation(true);
        setError('Please confirm that the data owner is a legal adult (18+ years)');
        return false;
      }
    }
    
    // Phone is always required when creating a data owner
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }
    // Normalize phone number before validation - remove spaces, dashes, etc.
    const normalizedPhone = formData.phoneNumber.replace(/[\s\-()]/g, '');
    const phoneError = validateOptionalPhoneNumber(normalizedPhone, formData.countryCode);
    if (phoneError) {
      setError(phoneError);
      return false;
    }
    
    if (formData.sendMethod === 'EMAIL' || formData.sendMethod === 'BOTH') {
      if (!formData.email.trim()) {
        setError('Email is required for email delivery');
        return false;
      }
      const emailError = validateOptionalEmail(formData.email);
      if (emailError) {
        setError(emailError);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const request: SendConsentRequest = {
        formId,
        dataOwner: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email || undefined,
          phoneNumber: formData.phoneNumber || undefined,
          countryCode: formData.countryCode,
          gender: formData.gender || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          createDataOwner: true,
        },
        evidenceTypes,
        successRedirectUrl,
        failureRedirectUrl,
        linkExpiryInSeconds: 86400, // 24 hours
        sendVia: formData.sendMethod,
        customMessage: formData.customMessage || undefined,
      };
      
      const response = await adminApi.sendConsentUrl(request);
      setGeneratedUrl(response.url);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to send consent request');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    // Reset state
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '+91',
      phoneNumber: '',
      gender: '',
      dateOfBirth: '',
      sendMethod: 'EMAIL',
      customMessage: '',
    });
    setError(null);
    setSuccess(false);
    setGeneratedUrl(null);
    setShowAgeConfirmation(false);
    setConfirmedAdult(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay $show={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <span>📤</span>
            Send Consent Request
          </ModalTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {formTitle && (
            <Section>
              <SectionTitle>📋 Form</SectionTitle>
              <div style={{ fontWeight: 500, color: '#1e293b' }}>{formTitle}</div>
            </Section>
          )}
          
          {error && (
            <AlertMessage $variant="error">
              ⚠️ {error}
            </AlertMessage>
          )}
          
          {success && generatedUrl ? (
            <SuccessBox>
              <SuccessTitle>
                <span>✅</span>
                Consent Request Created!
              </SuccessTitle>
              <p style={{ margin: '0 0 8px', color: '#166534', fontSize: '14px' }}>
                {formData.sendMethod === 'NONE' 
                  ? 'The consent URL has been generated. Copy and share it manually.'
                  : `The consent request will be sent to ${formData.firstName} via ${formData.sendMethod.toLowerCase()}.`}
              </p>
              <UrlBox>
                <UrlInput 
                  value={generatedUrl} 
                  readOnly 
                  onClick={(e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()}
                />
                <CopyButton onClick={copyToClipboard}>
                  {copied ? '✓ Copied!' : '📋 Copy'}
                </CopyButton>
              </UrlBox>
            </SuccessBox>
          ) : (
            <>
              {/* Recipient Information */}
              <Section>
                <SectionTitle>👤 Recipient Information</SectionTitle>
                <InputRow>
                  <FormGroup>
                    <Label>First Name *</Label>
                    <Input
                      type="text"
                      value={formData.firstName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('firstName', e.target.value)}
                      placeholder="John"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name *</Label>
                    <Input
                      type="text"
                      value={formData.lastName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('lastName', e.target.value)}
                      placeholder="Doe"
                    />
                  </FormGroup>
                </InputRow>
                
                <InputRow>
                  <FormGroup>
                    <Label>Gender (optional)</Label>
                    <Select
                      value={formData.gender}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('gender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                      <option value="N">Prefer not to say</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Date of Birth (optional)</Label>
                    <Input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('dateOfBirth', e.target.value)}
                    />
                    <HelpText>If not provided, you must confirm the person is 18+</HelpText>
                  </FormGroup>
                </InputRow>
                
                {/* Age confirmation checkbox - shows when no DOB */}
                {showAgeConfirmation && !formData.dateOfBirth && (
                  <div style={{ 
                    background: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: '8px', 
                    padding: '12px 16px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <input
                      type="checkbox"
                      id="adultConfirmation"
                      checked={confirmedAdult}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setConfirmedAdult(e.target.checked);
                        if (e.target.checked) setError(null);
                      }}
                      style={{ marginTop: '2px' }}
                    />
                    <label htmlFor="adultConfirmation" style={{ fontSize: '13px', color: '#92400e', cursor: 'pointer' }}>
                      <strong>I confirm</strong> that the data owner ({formData.firstName || 'this person'}) is a legal adult (18 years or older). 
                      This platform does not support consent collection for minors.
                    </label>
                  </div>
                )}
                
                <FormGroup>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('email', e.target.value)}
                    placeholder="john.doe@example.com"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <PhoneInputRow>
                    <CountryCodeSelect
                      value={formData.countryCode}
                      onChange={(value) => handleChange('countryCode', value)}
                    />
                    <Input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => handleChange('phoneNumber', e.target.value)}
                      placeholder="9876543210"
                    />
                  </PhoneInputRow>
                  <HelpText>Format: +country code followed by mobile number (e.g., +919876543210)</HelpText>
                </FormGroup>
              </Section>
              
              {/* Delivery Method */}
              <Section>
                <SectionTitle>📬 Delivery Method</SectionTitle>
                <SendMethodGroup>
                  <SendMethodOption $selected={formData.sendMethod === 'EMAIL'}>
                    <input
                      type="radio"
                      name="sendMethod"
                      checked={formData.sendMethod === 'EMAIL'}
                      onChange={() => handleChange('sendMethod', 'EMAIL')}
                    />
                    <MethodIcon>📧</MethodIcon>
                    <MethodInfo>
                      <MethodName>Email</MethodName>
                      <MethodDesc>Send via email</MethodDesc>
                    </MethodInfo>
                  </SendMethodOption>
                  
                  <SendMethodOption $selected={formData.sendMethod === 'SMS'}>
                    <input
                      type="radio"
                      name="sendMethod"
                      checked={formData.sendMethod === 'SMS'}
                      onChange={() => handleChange('sendMethod', 'SMS')}
                    />
                    <MethodIcon>📱</MethodIcon>
                    <MethodInfo>
                      <MethodName>SMS</MethodName>
                      <MethodDesc>Send via text</MethodDesc>
                    </MethodInfo>
                  </SendMethodOption>
                  
                  <SendMethodOption $selected={formData.sendMethod === 'BOTH'}>
                    <input
                      type="radio"
                      name="sendMethod"
                      checked={formData.sendMethod === 'BOTH'}
                      onChange={() => handleChange('sendMethod', 'BOTH')}
                    />
                    <MethodIcon>📨</MethodIcon>
                    <MethodInfo>
                      <MethodName>Both</MethodName>
                      <MethodDesc>Email + SMS</MethodDesc>
                    </MethodInfo>
                  </SendMethodOption>
                  
                  <SendMethodOption $selected={formData.sendMethod === 'NONE'}>
                    <input
                      type="radio"
                      name="sendMethod"
                      checked={formData.sendMethod === 'NONE'}
                      onChange={() => handleChange('sendMethod', 'NONE')}
                    />
                    <MethodIcon>🔗</MethodIcon>
                    <MethodInfo>
                      <MethodName>URL Only</MethodName>
                      <MethodDesc>Generate link</MethodDesc>
                    </MethodInfo>
                  </SendMethodOption>
                </SendMethodGroup>
                <HelpText>
                  {formData.sendMethod === 'NONE' 
                    ? 'Generate a consent URL to share manually'
                    : 'We\'ll send the consent request with a secure link'}
                </HelpText>
              </Section>
            </>
          )}
        </ModalBody>
        
        <ModalFooter>
          {success ? (
            <>
              <SecondaryButton onClick={handleClose}>
                Close
              </SecondaryButton>
              <PrimaryButton 
                onClick={() => {
                  setSuccess(false);
                  setGeneratedUrl(null);
                  setFormData(prev => ({ ...prev, firstName: '', lastName: '', email: '', phoneNumber: '', gender: '', dateOfBirth: '' }));
                }}
              >
                Send Another
              </PrimaryButton>
            </>
          ) : (
            <>
              <SecondaryButton onClick={handleClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>⏳ Sending...</>
                ) : formData.sendMethod === 'NONE' ? (
                  <>🔗 Generate URL</>
                ) : (
                  <>📤 Send Consent Request</>
                )}
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SendConsentModal;
